import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './Leaderboard.css';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

// --- CONFIGURATION ---
const WIKI_BASE_URL = "https://dayz.fandom.com/wiki/"; 

// --- INTERFACES ---
interface HitZones {
  Head: number; Torso: number; Legs: number; Arms: number; Other: number;
}

interface Player {
  steamId: string; name: string; avatar: string;
  playersKilled: number; infectedKilled: number; mutantKills: number;
  deaths: number; aiKills: number; animalKills: number;
  longestHit: number; averageHitDistance: number;
  playtime: number; totalDistance: number;
  topWeapon: string; hitZones: HitZones;
  weaponStats: Record<string, number> | string; 
  lastSeen: number;
}

interface FeedItem { id: number; time: number; message: string; steamId?: string; }
interface MetaItem { name: string; count: number; }
// UPDATED: Added factions to MetaData
interface MetaData { weapons: MetaItem[]; projectiles: MetaItem[]; factions: MetaItem[]; }
interface PlayerResponse { players: Player[]; lastUpdated: string; }
type SortKey = 'playersKilled' | 'infectedKilled' | 'longestHit' | 'playtime' | 'kd' | 'mutantKills' | 'lastSeen';

const WikiAxisTick = ({ x, y, payload }: any) => {
  return (
    <g transform={`translate(${x},${y})`}>
      <text 
        x={-5} 
        y={4} 
        textAnchor="end" 
        fill="#3498db" 
        fontSize={11}
        style={{ cursor: 'pointer', textDecoration: 'underline' }}
        onClick={() => window.open(`${WIKI_BASE_URL}${payload.value.replace(/ /g, '_')}`, '_blank')}
      >
        {payload.value.length > 18 ? payload.value.substring(0, 18) + '..' : payload.value}
      </text>
    </g>
  );
};

const Leaderboard: React.FC = () => {
  const [players, setPlayers] = useState<Player[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<SortKey>('playersKilled');
  const [feed, setFeed] = useState<FeedItem[]>([]);
  const [meta, setMeta] = useState<MetaData>({ weapons: [], projectiles: [], factions: [] });
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [adminToken, setAdminToken] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [rawLogs, setRawLogs] = useState<string[]>([]);

  const apiUrl = `/api/players`;
  const feedUrl = `/api/feed`;
  const metaUrl = `/api/meta`;

  useEffect(() => {
    const fetchData = () => {
      axios.get<PlayerResponse>(apiUrl).then(res => {
          setPlayers(res.data.players);
          if (res.data.lastUpdated) setLastUpdated(new Date(res.data.lastUpdated).toLocaleTimeString());
      }).catch(console.error);
    };
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    axios.get<FeedItem[]>(feedUrl).then(res => setFeed(res.data)).catch(console.error);
    axios.get<MetaData>(metaUrl).then(res => setMeta(res.data)).catch(console.error);
  }, [feedUrl, metaUrl]);

  const getKDNumber = (k: number, d: number) => d === 0 ? k : k / d;

  const timeAgo = (timestamp: number) => {
    if (!timestamp) return "Never";
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 60) return "Just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const formatPlaytime = (seconds: number) => {
    if (!seconds) return "0h 0m";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${h}h ${m}m`;
  };

  const formatDistance = (meters: number) => {
    if (!meters) return "0km";
    return (meters / 1000).toFixed(1) + "km";
  };

  const getPlayerWeaponData = (stats: any) => {
    if (!stats) return [];
    let safeStats = stats;
    if (typeof stats === 'string') {
        try { safeStats = JSON.parse(stats); } catch(e) { return []; }
    }
    if (!safeStats || Array.isArray(safeStats)) return [];
    return Object.entries(safeStats)
      .map(([name, count]) => ({ name, count: Number(count) }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  };

  const processedPlayers = useMemo(() => {
    return players
      .filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()))
      .sort((a, b) => {
        if (sortBy === 'kd') return getKDNumber(b.playersKilled, b.deaths) - getKDNumber(a.playersKilled, a.deaths);
        if (sortBy === 'playtime') return (b.playtime || 0) - (a.playtime || 0);
        return (b[sortBy] as any) - (a[sortBy] as any);
      });
  }, [players, searchTerm, sortBy]);

  const handleAdminUpdate = async (field: string, value: any) => {
    if (!selectedPlayer) return;
    try {
      await axios.post('/api/admin/update-player', { steamId: selectedPlayer.steamId, field, value, token: adminToken });
      alert("Updated! Refresh to see changes.");
    } catch (e) { alert("Error"); }
  };

  const fetchRawLogs = async (sid: string) => {
    try {
        const res = await axios.get(`/api/admin/logs/${sid}?token=${adminToken}`);
        setRawLogs(res.data);
    } catch (e) { alert("Error fetching logs"); }
  };

  const handleDelete = async () => {
    if (!selectedPlayer) return;
    if(window.confirm("Delete Player?")) {
        try {
            await axios.post('/api/admin/delete-player', { steamId: selectedPlayer.steamId, token: adminToken });
            setSelectedPlayer(null);
        } catch(e) { alert("Error"); }
    }
  };

  const handleFeedClick = (steamId?: string) => {
    if (!steamId) return;
    const player = players.find(p => p.steamId === steamId);
    if (player) {
      setSelectedPlayer(player);
      setEditMode(false);
    }
  };

  return (
    <div className="leaderboard-container">
      {/* KILL FEED */}
      <div className="kill-feed-container">
        <div className="kill-feed-track">
           {feed.length > 0 ? feed.map((log, i) => (
             <span 
               key={i} 
               className="feed-item" 
               onClick={() => handleFeedClick(log.steamId)}
               style={{ 
                 cursor: log.steamId ? 'pointer' : 'default',
                 color: log.steamId ? '#f1c40f' : 'inherit',
                 textDecoration: log.steamId ? 'underline' : 'none',
                 textUnderlineOffset: '4px'
               }}
               title={log.steamId ? "Click to view killer's profile" : ""}
             >
               💀 {log.message}
             </span>
           )) : <span className="feed-item">Waiting for combat data...</span>}
        </div>
      </div>

      {/* META STATS CHARTS */}
      <div className="meta-container" style={{ display: 'flex', gap: '20px', marginBottom: '30px', flexWrap: 'wrap' }}>
          <div className="chart-box" style={{ flex: 1, minWidth: '300px', background: '#1e1e1e', padding: '20px', borderRadius: '8px', border: '1px solid #333' }}>
              <h4 style={{ color: '#aaa', margin: '0 0 15px 0', textTransform: 'uppercase', fontSize: '0.9rem' }}>Top Weapons</h4>
              <div style={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer>
                      <BarChart layout="vertical" data={meta.weapons} margin={{ left: 20, right: 30 }}>
                          <XAxis type="number" hide />
                          <YAxis dataKey="name" type="category" width={140} interval={0} tick={<WikiAxisTick />} />
                          <Tooltip contentStyle={{ backgroundColor: '#333', border: 'none', color: '#fff' }} />
                          <Bar dataKey="count" fill="#3498db" radius={[0, 4, 4, 0]} barSize={20}>
                            <Cell fill="#3498db" />
                          </Bar>
                      </BarChart>
                  </ResponsiveContainer>
              </div>
          </div>
          <div className="chart-box" style={{ flex: 1, minWidth: '300px', background: '#1e1e1e', padding: '20px', borderRadius: '8px', border: '1px solid #333' }}>
              <h4 style={{ color: '#aaa', margin: '0 0 15px 0', textTransform: 'uppercase', fontSize: '0.9rem' }}>Top Ammo</h4>
              <div style={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer>
                      <BarChart layout="vertical" data={meta.projectiles} margin={{ left: 20, right: 30 }}>
                          <XAxis type="number" hide />
                          <YAxis dataKey="name" type="category" width={140} interval={0} tick={<WikiAxisTick />} />
                          <Tooltip contentStyle={{ backgroundColor: '#333', border: 'none', color: '#fff' }} />
                          <Bar dataKey="count" fill="#e74c3c" radius={[0, 4, 4, 0]} barSize={20}>
                            <Cell fill="#e74c3c" />
                          </Bar>
                      </BarChart>
                  </ResponsiveContainer>
              </div>
          </div>
          
          {/* NEW: FACTION DEATHS CHART */}
          <div className="chart-box" style={{ flex: 1, minWidth: '300px', background: '#1e1e1e', padding: '20px', borderRadius: '8px', border: '1px solid #333' }}>
              <h4 style={{ color: '#aaa', margin: '0 0 15px 0', textTransform: 'uppercase', fontSize: '0.9rem' }}>Faction Deaths</h4>
              <div style={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer>
                      <BarChart layout="vertical" data={meta.factions} margin={{ left: 20, right: 30 }}>
                          <XAxis type="number" hide />
                          <YAxis dataKey="name" type="category" width={100} tick={{fill: '#fff', fontSize: 11}} interval={0} />
                          <Tooltip contentStyle={{ backgroundColor: '#333', border: 'none', color: '#fff' }} />
                          <Bar dataKey="count" fill="#2ecc71" radius={[0, 4, 4, 0]} barSize={20}>
                            <Cell fill="#2ecc71" />
                          </Bar>
                      </BarChart>
                  </ResponsiveContainer>
              </div>
          </div>
      </div>

      {/* HEADER CONTROLS */}
      <div className="header-flex">
        <h2 className="title" style={{ margin: 0 }}>Black Legion S.T.A.L.K.E.R.S</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value as SortKey)}>
            <option value="playersKilled">Sort by: PVP Kills</option>
            <option value="kd">Sort by: K/D Ratio</option>
            <option value="longestHit">Sort by: Longest Hit</option>
            <option value="infectedKilled">Sort by: Infected Kills</option>
            <option value="mutantKills">Sort by: Mutant Kills</option>
            <option value="playtime">Sort by: Playtime</option>
            <option value="lastSeen">Sort by: Last Active</option>
          </select>
          <input type="text" placeholder="Search Survivor..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
      </div>
      
      {/* MAIN TABLE */}
      <table className="main-table">
        <thead>
          <tr>
            <th>Rank</th><th>Name</th><th>PVP Kills</th><th>K/D Ratio</th><th>Infected</th><th>Mutants</th><th>Avg Distance</th><th>Weapon</th><th>Last Active</th>
          </tr>
        </thead>
        <tbody>
          {processedPlayers.map((player, index) => (
            <tr key={player.steamId} onClick={() => { setSelectedPlayer(player); setEditMode(false); }} className="clickable-row">
              <td className="rank-cell">#{index + 1}</td>
              <td className="name-cell">
                <div style={{ display: 'flex', alignItems: 'center', height: '100%', gap: '12px' }}>
                  {player.avatar ? <img src={player.avatar} alt="avatar" className="player-avatar-small" /> : <div className="avatar-placeholder">{player.name.charAt(0)}</div>}
                  {player.name}
                </div>
              </td>
              <td className="highlight">{player.playersKilled}</td>
              <td style={{ fontWeight: 'bold', color: '#f1c40f' }}>{getKDNumber(player.playersKilled, player.deaths).toFixed(2)}</td>
              <td>{player.infectedKilled}</td>
              <td>{player.mutantKills || 0}</td>
              <td style={{ fontSize: '0.9rem', color: '#888' }}>{player.averageHitDistance ? player.averageHitDistance + "m" : "0m"}</td>
              <td className="weapon-cell">
                <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                  {player.topWeapon !== "Fists" ? (
                    <a 
                      href={`${WIKI_BASE_URL}${player.topWeapon.replace(/ /g, '_')}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="wiki-link-table"
                      style={{ color: '#3498db', textDecoration: 'none', fontWeight: 'bold' }}
                      onClick={(e) => e.stopPropagation()} 
                    >
                      {player.topWeapon}
                    </a>
                  ) : (
                    <span>{player.topWeapon}</span>
                  )}
                </div>
              </td>
              <td>
                <div style={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'flex-end', color: '#888', fontSize: '0.85rem' }}>{timeAgo(player.lastSeen)}</div>
              </td>
            </tr>
          ))}
          {processedPlayers.length === 0 && (
            <tr><td colSpan={9} style={{ padding: '40px', color: '#666', textAlign: 'center' }}>No survivors found.</td></tr>
          )}
        </tbody>
      </table>

      {lastUpdated && <div className="footer-timestamp">Last Synced: {lastUpdated}</div>}

      {/* DETAIL MODAL */}
      {selectedPlayer && (
        <div className="modal-overlay" onClick={() => setSelectedPlayer(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="close-btn" onClick={() => setSelectedPlayer(null)}>X</button>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    {selectedPlayer.avatar && <img src={selectedPlayer.avatar} alt="profile" className="modal-avatar" style={{ width: '64px', borderRadius: '8px' }} />}
                    <div>
                        <h3 style={{ margin: 0 }}>{selectedPlayer.name}</h3>
                        <span className="steam-id" style={{ color: '#888', fontSize: '0.85rem' }}>{selectedPlayer.steamId}</span>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={() => setEditMode(!editMode)} className="admin-toggle">🛠 Admin</button>
                  <a href={`https://steamcommunity.com/profiles/${selectedPlayer.steamId}`} target="_blank" rel="noopener noreferrer" className="steam-link-btn">View Steam</a>
                </div>
            </div>

            {editMode ? (
              <div className="admin-panel">
                <input type="password" placeholder="Admin Token" value={adminToken} onChange={e => setAdminToken(e.target.value)} style={{ marginBottom: '15px', width: '100%' }} />
                <div className="admin-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <button style={{ background: '#2ecc71', color: '#000', fontWeight: 'bold' }} onClick={async () => { try { await axios.get(`/api/test-sync`); alert("Global Sync Triggered Successfully!"); } catch (e) { alert("Sync Failed"); } }}>🔄 Force Global Sync</button>
                    <button style={{ background: '#e67e22', color: '#000', fontWeight: 'bold' }} onClick={async () => { if(window.confirm("Reset Weapon/Ammo Charts?")) { await axios.post('/api/admin/reset-meta', { token: adminToken }); alert("Charts Cleared. Run Sync to repopulate."); } }}>📉 Reset Global Charts</button>
                    <button style={{ background: '#d35400', color: '#fff', fontWeight: 'bold' }} onClick={() => handleAdminUpdate('weaponStats', {})}>🧹 Reset Wpn Stats</button>
                    <button onClick={() => handleAdminUpdate('playersKilled', 0)}>Reset PVP</button>
                    <button onClick={() => handleAdminUpdate('infectedKilled', 0)}>Reset Infected</button>
                    <button onClick={() => fetchRawLogs(selectedPlayer.steamId)}>Raw Logs</button>
                    <button style={{ background: '#e74c3c', gridColumn: 'span 2' }} onClick={handleDelete}>Delete Player</button>
                </div>
                {rawLogs.length > 0 && (
                  <div className="log-viewer">
                    {rawLogs.map((log, i) => <pre key={i}>{JSON.stringify(log, null, 2)}</pre>)}
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="stats-grid">
                  <div className="stat-card pvp"><h4>PVP Kills</h4><p>{selectedPlayer.playersKilled}</p></div>
                  <div className="stat-card pve"><h4>Infected</h4><p>{selectedPlayer.infectedKilled}</p></div>
                  <div className="stat-card" style={{borderColor: '#9b59b6'}}><h4>Mutants</h4><p>{selectedPlayer.mutantKills || 0}</p></div>
                  <div className="stat-card"><h4>AI / Bots</h4><p>{selectedPlayer.aiKills || 0}</p></div>
                  <div className="stat-card"><h4>Animals</h4><p>{selectedPlayer.animalKills || 0}</p></div>
                  <div className="stat-card"><h4>Longest Shot</h4><p>{selectedPlayer.longestHit ? selectedPlayer.longestHit + "m" : "N/A"}</p></div>
                  
                  <div className="stat-card"><h4>Playtime</h4><p>{formatPlaytime(selectedPlayer.playtime)}</p></div>
                  <div className="stat-card"><h4>Travelled</h4><p>{formatDistance(selectedPlayer.totalDistance)}</p></div>
                  
                  <div className="stat-card"><h4>Avg Distance</h4><p>{selectedPlayer.averageHitDistance ? selectedPlayer.averageHitDistance + "m" : "0m"}</p></div>
                  <div className="stat-card"><h4>Last Active</h4><p style={{fontSize: '1rem', marginTop: '10px'}}>{timeAgo(selectedPlayer.lastSeen)}</p></div>
                </div>

                <div className="charts-flex-container" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '30px' }}>
                    {selectedPlayer.hitZones && (
                      <div className="chart-container" style={{ background: '#2a2a2a', padding: '20px', borderRadius: '8px' }}>
                          <h4 style={{ color: '#aaa', marginBottom: '15px', textTransform: 'uppercase' }}>Hit Zone Accuracy</h4>
                          <div style={{ width: '100%', height: 250 }}>
                              <ResponsiveContainer>
                                  <BarChart data={[
                                      { name: 'Head', count: selectedPlayer.hitZones.Head || 0 },
                                      { name: 'Torso', count: selectedPlayer.hitZones.Torso || 0 },
                                      { name: 'Arms', count: selectedPlayer.hitZones.Arms || 0 },
                                      { name: 'Legs', count: selectedPlayer.hitZones.Legs || 0 },
                                  ]}>
                                      <XAxis dataKey="name" stroke="#888" />
                                      <YAxis stroke="#888" />
                                      <Tooltip contentStyle={{ backgroundColor: '#333', border: 'none', color: '#fff' }} />
                                      <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                                          <Cell fill="#e74c3c" /> <Cell fill="#3498db" /> <Cell fill="#f1c40f" /> <Cell fill="#2ecc71" />
                                      </Bar>
                                  </BarChart>
                              </ResponsiveContainer>
                          </div>
                      </div>
                    )}

                    <div className="chart-container" style={{ background: '#2a2a2a', padding: '20px', borderRadius: '8px' }}>
                        <h4 style={{ color: '#aaa', marginBottom: '15px', textTransform: 'uppercase' }}>MOST USED WEAPONS</h4>
                        <div style={{ width: '100%', height: 250 }}>
                            <ResponsiveContainer>
                                <BarChart layout="vertical" data={getPlayerWeaponData(selectedPlayer.weaponStats)} margin={{ left: 20, right: 20 }}>
                                    <XAxis type="number" hide />
                                    <YAxis dataKey="name" type="category" width={130} interval={0} tick={<WikiAxisTick />} />
                                    <Tooltip contentStyle={{ backgroundColor: '#333', border: 'none', color: '#fff' }} />
                                    <Bar dataKey="count" fill="#9b59b6" radius={[0, 4, 4, 0]} barSize={20}>
                                        <Cell fill="#9b59b6" />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Leaderboard;